import React, { useState } from 'react';
import { Shield, FileText, AlertTriangle, CheckCircle, Eye, Download, Filter } from 'lucide-react';

const SecurityCompliance = () => {
  const [activeTab, setActiveTab] = useState('audit');

  const auditLogs = [
    {
      id: 1,
      timestamp: '2024-01-15 14:30:25',
      user: 'admin@vitalaudio.com',
      action: 'User Created',
      resource: 'User: jane.doe@hospital.com',
      ip: '192.168.1.100',
      status: 'success'
    },
    {
      id: 2,
      timestamp: '2024-01-15 14:25:10',
      user: 'admin@vitalaudio.com',
      action: 'Tenant Updated',
      resource: 'Tenant: City Medical Center',
      ip: '192.168.1.100',
      status: 'success'
    },
    {
      id: 3,
      timestamp: '2024-01-15 14:20:05',
      user: 'unknown',
      action: 'Login Failed',
      resource: 'Authentication',
      ip: '10.0.0.45',
      status: 'failed'
    }
  ];

  const securityReports = [
    {
      id: 1,
      name: 'HIPAA Compliance Check',
      status: 'passed',
      lastRun: '2024-01-15',
      score: 98,
      issues: 2
    },
    {
      id: 2,
      name: 'Data Encryption Audit',
      status: 'passed',
      lastRun: '2024-01-15',
      score: 100,
      issues: 0
    },
    {
      id: 3,
      name: 'Access Control Review',
      status: 'warning',
      lastRun: '2024-01-14',
      score: 85,
      issues: 5
    }
  ];

  const complianceChecks = [
    {
      category: 'Data Protection',
      checks: [
        { name: 'Data Encryption at Rest', status: 'passed', description: 'All patient data encrypted using AES-256' },
        { name: 'Data Encryption in Transit', status: 'passed', description: 'TLS 1.3 enforced for all connections' },
        { name: 'Data Backup Encryption', status: 'passed', description: 'Backups encrypted and stored securely' },
        { name: 'Data Retention Policy', status: 'warning', description: 'Policy needs review for compliance' }
      ]
    },
    {
      category: 'Access Control',
      checks: [
        { name: 'Role-Based Access Control', status: 'passed', description: 'RBAC implemented across all modules' },
        { name: 'Multi-Factor Authentication', status: 'warning', description: 'MFA not enforced for all users' },
        { name: 'Session Management', status: 'passed', description: 'Secure session handling implemented' },
        { name: 'Password Policies', status: 'passed', description: 'Strong password requirements enforced' }
      ]
    },
    {
      category: 'Audit & Monitoring',
      checks: [
        { name: 'Audit Log Collection', status: 'passed', description: 'All user actions logged' },
        { name: 'Log Integrity', status: 'passed', description: 'Logs protected from tampering' },
        { name: 'Real-time Monitoring', status: 'failed', description: 'Monitoring system needs configuration' },
        { name: 'Incident Response', status: 'warning', description: 'Response procedures need documentation' }
      ]
    }
  ];

  const TabButton = ({ id, label, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`px-4 py-2 font-medium rounded-lg transition-colors ${
        active
          ? 'bg-[#DCEAF5] text-[#207CE5] border border-[#207CE5]'
          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );

  const StatusBadge = ({ status }) => {
    const styles = {
      passed: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      success: 'bg-green-100 text-green-800'
    };

    const icons = {
      passed: CheckCircle,
      warning: AlertTriangle,
      failed: AlertTriangle,
      success: CheckCircle
    };

    const Icon = icons[status] || AlertTriangle;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security & Compliance</h1>
          <p className="text-gray-600">Monitor security posture and compliance status</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Security Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                    <div className="p-3 bg-[#DCEAF5] rounded-lg">
          <Shield className="h-6 w-6 text-[#008080]" />
        </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Security Score</p>
              <p className="text-2xl font-bold text-gray-900">94/100</p>
              <p className="text-xs text-green-600">Excellent</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                    <div className="p-3 bg-[#DCEAF5] rounded-lg">
          <FileText className="h-6 w-6 text-[#207CE5]" />
        </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Compliance Status</p>
              <p className="text-2xl font-bold text-gray-900">HIPAA</p>
              <p className="text-xs text-blue-600">Compliant</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                    <div className="p-3 bg-[#DCEAF5] rounded-lg">
          <AlertTriangle className="h-6 w-6 text-[#4682B4]" />
        </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Open Issues</p>
              <p className="text-2xl font-bold text-gray-900">7</p>
              <p className="text-xs text-yellow-600">Needs attention</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <TabButton id="audit" label="Audit Logs" active={activeTab === 'audit'} onClick={setActiveTab} />
        <TabButton id="reports" label="Security Reports" active={activeTab === 'reports'} onClick={setActiveTab} />
        <TabButton id="compliance" label="Compliance Checks" active={activeTab === 'compliance'} onClick={setActiveTab} />
      </div>

      {/* Tab Content */}
      {activeTab === 'audit' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Audit Logs</h2>
              <div className="flex items-center space-x-3">
                <select className="px-3 py-1 border border-gray-300 rounded text-sm">
                  <option>All Actions</option>
                  <option>User Actions</option>
                  <option>System Actions</option>
                  <option>Failed Actions</option>
                </select>
                <button className="text-gray-400 hover:text-gray-600">
                  <Filter className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.timestamp}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.user}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.action}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.resource}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ip}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={log.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="grid grid-cols-1 gap-6">
          {securityReports.map((report) => (
            <div key={report.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{report.name}</h3>
                    <p className="text-sm text-gray-500">Last run: {report.lastRun}</p>
                  </div>
                  <StatusBadge status={report.status} />
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">Score: {report.score}/100</p>
                    <p className="text-sm text-gray-500">{report.issues} issues found</p>
                  </div>
                  <button className="text-[#207CE5] hover:text-[#0B1C2C]">
                    <Eye className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'compliance' && (
        <div className="space-y-6">
          {complianceChecks.map((category) => (
            <div key={category.category} className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">{category.category}</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {category.checks.map((check, index) => (
                    <div key={index} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-medium text-gray-900">{check.name}</h4>
                          <StatusBadge status={check.status} />
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{check.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SecurityCompliance;